import React, { useState, useEffect } from 'react';
import {
  CheckSquare,
  Clock,
  Calendar,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  FileText,
  Target,
  Users,
  BarChart3,
  Plus,
  Timer
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { UserDashboardLayout } from './UserDashboardLayout';
import { AuthDebug } from '../../components/AuthDebug';
import ErrorBoundary from '../../components/ErrorBoundary';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface UserDashboardOverview {
  tasks: {
    assigned: number;
    completed: number;
    overdue: number;
    inProgress: number;
  };
  timeTracking: {
    hoursToday: number;
    hoursThisWeek: number;
    hoursThisMonth: number;
    utilizationRate: number;
  };
  productivity: {
    completionRate: number;
    avgTaskTime: number;
    tasksCompletedThisWeek: number;
  };
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: string;
  }>;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  icon: React.ElementType;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, change, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change && (
          <p className={`text-sm flex items-center mt-1 ${
            trend === 'up' ? 'text-green-600' : 
            trend === 'down' ? 'text-red-600' : 
            'text-gray-600'
          }`}>
            {trend === 'up' && <TrendingUp className="w-4 h-4 mr-1" />}
            {change}
          </p>
        )}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const QuickActionCard: React.FC<{
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  onClick: () => void;
}> = ({ title, description, icon: Icon, color, onClick }) => (
  <div 
    className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
    onClick={onClick}
  >
    <div className="flex items-start space-x-4">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h3 className="font-medium text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  </div>
);

export const UserDashboard: React.FC = () => {
  const { user, isAuthenticated, tenant, accessToken } = useAuthStore();
  const navigate = useNavigate();
  const { login } = useAuthStore();

  // Check authentication and redirect if needed
  React.useEffect(() => {
    console.log('🔍 Dashboard Auth Check:', {
      isAuthenticated,
      hasUser: !!user,
      hasToken: !!accessToken,
      hasTenant: !!tenant,
      userEmail: user?.email
    });

    // Don't redirect immediately, let user see the debug info and login button
    if (!isAuthenticated || !user || !accessToken) {
      console.log('❌ User not authenticated');
    }
  }, [isAuthenticated, user, accessToken, navigate]);

  // Quick login function for testing
  const handleQuickLogin = async () => {
    try {
      console.log('🔄 Attempting quick login...');
      await login('admin@demo.com', 'Demo123!', 'demo');
      toast.success('Login successful!');
    } catch (error) {
      console.error('❌ Login failed:', error);
      toast.error('Login failed. Please check credentials.');
    }
  };
  const [overview, setOverview] = useState<UserDashboardOverview>({
    tasks: {
      assigned: 0,
      completed: 0,
      overdue: 0,
      inProgress: 0,
    },
    timeTracking: {
      hoursToday: 0,
      hoursThisWeek: 0,
      hoursThisMonth: 0,
      utilizationRate: 0,
    },
    productivity: {
      completionRate: 0,
      avgTaskTime: 0,
      tasksCompletedThisWeek: 0,
    },
    recentActivity: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (isMounted) {
        await fetchDashboardData();
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user, accessToken]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching dashboard overview data...');

      const response = await api.get('/users/dashboard-overview');
      console.log('✅ Dashboard overview response:', response.data);

      setOverview(response.data.data || response.data); // Extract data from the response
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);

      // Use mock data as fallback
      console.log('🔄 Using mock dashboard data as fallback');
      const mockOverview: UserDashboardOverview = {
        tasks: {
          assigned: 12,
          completed: 8,
          overdue: 2,
          inProgress: 4,
        },
        timeTracking: {
          hoursToday: 7.5,
          hoursThisWeek: 35.5,
          hoursThisMonth: 142.5,
          utilizationRate: 85,
        },
        productivity: {
          completionRate: 75,
          avgTaskTime: 4.2,
          tasksCompletedThisWeek: 12,
        },
        recentActivity: [
          { type: 'task', description: 'Completed task: Review project proposal', timestamp: new Date().toISOString() },
          { type: 'task', description: 'Started task: Update documentation', timestamp: new Date(Date.now() - 3600000).toISOString() },
          { type: 'timesheet', description: 'Submitted timesheet for approval', timestamp: new Date(Date.now() - 7200000).toISOString() },
        ],
      };

      setOverview(mockOverview);
      toast('Using demo data - backend unavailable', { icon: 'ℹ️' });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'view_tasks':
        navigate('/user-dashboard/tasks');
        break;
      case 'start_timer':
        navigate('/user-dashboard/timesheet');
        break;
      case 'view_calendar':
        navigate('/user-dashboard/calendar');
        break;
      case 'view_reports':
        navigate('/user-dashboard/reports');
        break;
      default:
        toast('Feature coming soon!', { icon: 'ℹ️' });
    }
  };

  const isManager = user?.roles?.includes('Manager');

  // Show login prompt if not authenticated
  if (!isAuthenticated || !user || !accessToken) {
    return (
      <UserDashboardLayout>
        <div className="space-y-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
            <p className="text-gray-600 mb-6">Please log in to access your dashboard</p>
            <button
              onClick={handleQuickLogin}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Quick Login (Demo)
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Using demo credentials: admin@demo.com / Demo123! / firm: demo
            </p>
          </div>
          <AuthDebug />
        </div>
      </UserDashboardLayout>
    );
  }

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <ErrorBoundary>
        <div className="p-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.firstName}!
          </h2>
          <p className="text-gray-600">
            {isManager 
              ? "Here's an overview of your tasks and team performance."
              : "Here's an overview of your tasks and productivity."
            }
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Assigned Tasks"
            value={overview.tasks.assigned}
            change={`${overview.tasks.inProgress} in progress`}
            icon={CheckSquare}
            color="bg-blue-500"
            trend="neutral"
          />
          <StatCard
            title="Completed Tasks"
            value={overview.tasks.completed}
            change={`${overview.productivity.tasksCompletedThisWeek} this week`}
            icon={CheckCircle}
            color="bg-green-500"
            trend="up"
          />
          <StatCard
            title="Hours This Week"
            value={`${overview.timeTracking.hoursThisWeek}h`}
            change={`${overview.timeTracking.hoursToday}h today`}
            icon={Clock}
            color="bg-purple-500"
            trend="up"
          />
          <StatCard
            title="Completion Rate"
            value={`${overview.productivity.completionRate}%`}
            change={`${overview.productivity.avgTaskTime}h avg`}
            icon={Target}
            color="bg-orange-500"
            trend="up"
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionCard
              title="View My Tasks"
              description="See all your assigned tasks and deadlines"
              icon={CheckSquare}
              color="bg-blue-500"
              onClick={() => handleQuickAction('view_tasks')}
            />
            <QuickActionCard
              title="Start Timer"
              description="Track time for your current task"
              icon={Timer}
              color="bg-green-500"
              onClick={() => handleQuickAction('start_timer')}
            />
            <QuickActionCard
              title="View Calendar"
              description="Check your schedule and appointments"
              icon={Calendar}
              color="bg-purple-500"
              onClick={() => handleQuickAction('view_calendar')}
            />

            {isManager && (
              <QuickActionCard
                title="Team Reports"
                description="View team performance and analytics"
                icon={BarChart3}
                color="bg-orange-500"
                onClick={() => handleQuickAction('view_reports')}
              />
            )}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Task Overview */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Task Overview</h3>
              <CheckSquare className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">In Progress</span>
                <span className="font-medium text-blue-600">{overview.tasks.inProgress}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Overdue</span>
                <span className="font-medium text-red-600">{overview.tasks.overdue}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed</span>
                <span className="font-medium text-green-600">{overview.tasks.completed}</span>
              </div>
            </div>
          </div>

          {/* Time Tracking */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Time Tracking</h3>
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Today</span>
                <span className="font-medium">{overview.timeTracking.hoursToday}h</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">This Month</span>
                <span className="font-medium">{overview.timeTracking.hoursThisMonth}h</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Utilization Rate</span>
                <span className="font-medium text-primary-600">{overview.timeTracking.utilizationRate}%</span>
              </div>
            </div>
          </div>
        </div>


        </div>
        <AuthDebug />
      </ErrorBoundary>
    </UserDashboardLayout>
  );
};
